import { inject, Injectable } from '@angular/core';
import {
  DataModel,
  RestService,
  RestSettings,
} from '@infodata-group/inf-commons';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, map, tap } from 'rxjs/operators';
import { ToolsDtoFactory } from '../../engine/tools/tools-dto.factory';
import { BoCartographyGroupInterface } from '../../module/cartography/interface/back-office/bo-cartography-group.interface';
import { BoCartographyPositionMatInterface } from '../../module/cartography/interface/back-office/cartography-position/bo-cartography-position-mat.interface';
import { BoCartographyResourceMatInterface } from '../../module/cartography/interface/back-office/cartography-resource/bo-cartography-resource-mat.interface';
import {
  MapTagsClearAll,
  MapTagsDeleteDataModel,
  MapTagsDeleteGroups,
  MapTagsDeletePositions,
  MapTagsDeleteTags,
  MapTagsLoadDataModel,
  MapTagsLoadGroups,
  MapTagsLoadGroupsAndResources,
  MapTagsLoadPositions,
  MapTagsLoadTags,
} from '../actions/map-tags.actions';

const REST_SETTINGS: RestSettings = {
  showLoading: false,
};
const DEFAULT_STATE = {
  resources: [],
  resourcesDateLoad: null,
  isResourcesLoading: false,
  groups: [],
  groupsDateLoad: null,
  isGroupsLoading: false,
  positions: [],
  positionsDateLoad: null,
  isPositionsLoading: false,
  dataModel: new DataModel(),
  dataModelDateLoad: null,
  isDataModelLoading: false,
};

export class MapTagsStateModel {
  resources: BoCartographyResourceMatInterface[] = [];
  resourcesDateLoad: number;
  isResourcesLoading: boolean;
  groups: BoCartographyGroupInterface[] = [];
  groupsDateLoad: number;
  isGroupsLoading: boolean;
  positions: BoCartographyPositionMatInterface[] = [];
  positionsDateLoad: number;
  isPositionsLoading: boolean;
  dataModel: DataModel;
  dataModelDateLoad: number;
  isDataModelLoading: boolean;
}

@State<MapTagsStateModel>({
  name: 'mapMats',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class MapTagsState {
  private restService = inject(RestService);

  @Action(MapTagsLoadGroups)
  loadGroups(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      isGroupsLoading: true,
    });
    return this.restService
      .getAll(ToolsDtoFactory.getCartographyGroupMatDtoFactory(), REST_SETTINGS)
      .pipe(
        tap((groups) => {
          if (!groups.find((g) => g.id === 'XXX')) {
            groups.push({
              id: 'XXX',
              label: 'Groupe inconnu',
            });
          }
          state.patchState({
            groups: groups,
            groupsDateLoad: new Date().getTime(),
            isGroupsLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isGroupsLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapTagsLoadTags)
  loadResources(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      isResourcesLoading: true,
    });
    return this.restService
      .getAll(
        ToolsDtoFactory.getCartographyResourceMatDtoFactory(),
        REST_SETTINGS,
      )
      .pipe(
        tap((tags) => {
          state.patchState({
            resources: tags,
            resourcesDateLoad: new Date().getTime(),
            isResourcesLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isResourcesLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapTagsDeleteTags)
  deleteResources(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      resources: [],
      resourcesDateLoad: null,
    });
  }

  @Action(MapTagsDeleteGroups)
  deleteGroups(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      groups: [],
      groupsDateLoad: null,
    });
  }

  @Action(MapTagsLoadPositions)
  refreshPositions(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      isPositionsLoading: true,
    });
    return this.restService
      .getAll(ToolsDtoFactory.getCartographyPositionTagFactory(), REST_SETTINGS)
      .pipe(
        tap((positions) => {
          state.patchState({
            positions: positions,
            positionsDateLoad: new Date().getTime(),
            isPositionsLoading: false,
          });
        }),
        catchError(() => {
          state.patchState({
            isPositionsLoading: false,
          });
          return [];
        }),
      );
  }

  @Action(MapTagsDeletePositions)
  deletePositions(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      positions: [],
      positionsDateLoad: null,
    });
  }

  @Action(MapTagsLoadGroupsAndResources)
  loadGroupsAndResources(state: StateContext<MapTagsStateModel>) {
    state.dispatch(new MapTagsLoadGroups()).subscribe({
      next: () => {
        state.dispatch(new MapTagsLoadTags());
      },
    });
  }

  @Action(MapTagsLoadDataModel)
  loadDataModelTag(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      isDataModelLoading: true,
    });
    return this.restService
      .options(
        `${ToolsDtoFactory.getCartographyResourceMatDtoFactory().getApi()}?PRF=INFOBULLE`,
        REST_SETTINGS,
      )
      .pipe(
        map((model) => {
          const dateProperty = model.find('date');
          if (dateProperty) {
            dateProperty.id = 'dateText';
          }
          return model;
        }),
        tap((dataModel) => {
          state.patchState({
            dataModel: dataModel,
            dataModelDateLoad: new Date().getTime(),
            isDataModelLoading: false,
          });
        }),
      );
  }

  @Action(MapTagsDeleteDataModel)
  deleteDataModelTag(state: StateContext<MapTagsStateModel>) {
    state.patchState({
      dataModel: new DataModel(),
      dataModelDateLoad: null,
    });
  }

  @Action(MapTagsClearAll)
  clearAll(state: StateContext<MapTagsStateModel>) {
    state.patchState(DEFAULT_STATE);
  }
}
